import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useRef } from "react";
import { DLQInfoResult, DLQItem, Stage } from "../lusca-search-api/generated-src";
import { DlqInfoApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { DlqStatus } from "../components/dlq/DlqStatusIndicator";

export interface DlqInfo {
  status: DlqStatus;
  dlqItems?: DLQItem[];
  error: Error | null;
}

/*
 * This hook manages the lifecycle of an Athena query to check DLQ status:
 * 1. Initial call starts an Athena query and returns a queryExecutionId
 * 2. Performs immediate status check in case results are in Athena's cache
 * 3. If not immediately available, polls every second until query completes
 */
export default function useFetchDlqInfo(
  stage: Stage,
  orderId: string
): DlqInfo {
  const DlqApi = DlqInfoApiFactory();
  const queryClient = useQueryClient();
  const hasTriggeredImmediateFetch = useRef(false);

  const { data, error } = useQuery<DLQInfoResult, Error>(
    ["dlqInfo", stage, orderId],
    async () => {
      // Check cache for existing queryExecutionId from previous API responses
      // This is important for checking status of an ongoing query
      const currentData = queryClient.getQueryData<DLQInfoResult>([
        "dlqInfo",
        stage,
        orderId,
      ]);

      const queryExecutionId = currentData?.queryExecutionId;

      // First call (queryExecutionId is undefined):
      // - Initiates the DLQ query
      // - Returns a queryExecutionId
      //
      // Subsequent calls (queryExecutionId exists):
      // - Uses queryExecutionId to check status of the running query
      // - Returns current state (PENDING, COMPLETED, etc.)
      const { data }: AxiosResponse<DLQInfoResult> = await DlqApi.getDLQInfo(
        stage,
        orderId,
        queryExecutionId
      );

      return data;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!stage && !!orderId,
      refetchInterval: (data) => {
        // Once we have a PENDING status, we need to keep polling the query result
        if (data?.status === "PENDING") {
          // Special handling for when we first get a queryExecutionId
          // Performs immediate check for results incase Athena has it cached,
          // instead of waiting for the normal 1-second polling interval
          if (data.queryExecutionId && !hasTriggeredImmediateFetch.current) {
            // We only want to do this immediate check once per query
            hasTriggeredImmediateFetch.current = true;

            setTimeout(() => {
              queryClient
                .invalidateQueries(["dlqInfo", stage, orderId])
                .catch((error) => {
                  console.error("Error invalidating queries:", error);
                });
            }, 0);
          }

          // If result wasn't in Athena's cache, keep polling every second
          // until we get a final status
          return 1000;
        }
        return false; // Once we're no longer PENDING, stop polling
      },

      onSuccess: (data) => {
        if (data.status === "PENDING" && data.queryExecutionId) {
          queryClient.setQueryData<DLQInfoResult>(
            ["dlqInfo", stage, orderId],
            (oldData) => ({
              ...oldData,
              ...data,
            })
          );
        }
      },
      onError: () => {
        queryClient.setQueryData<DLQInfoResult>(
          ["dlqInfo", stage, orderId],
          () => ({ status: "FAILED" } as DLQInfoResult)
        );
      },
    }
  );

  const status: DlqStatus = (() => {
    if (error) return "error";
    if (!data) return "loading";

    switch (data.status) {
      case "PENDING":
        return "loading";
      case "COMPLETED":
        return data.inDLQ ? "inDlq" : "notInDlq";
      case "ERROR":
        return "error";
      default:
        return "error";
    }
  })();

  return {
    status,
    dlqItems: data?.dlqItems,
    error: error || null,
  };
}
